/**
 * Client do admin-backend (admin.futurabroker.com) para a página /admin-access.
 *
 * ATENÇÃO: NÃO usa o apiClient do api.service — aquele injeta o sigma_token
 * (sessão de usuário da Trading API) e redireciona para /login em 401.
 * Aqui a autenticação é o JWT ADMIN do admin-backend (email+senha+2FA),
 * guardado em chave separada do localStorage.
 */

const isProduction = typeof window !== 'undefined' && window.location.hostname !== 'localhost'
const ADMIN_API_BASE = isProduction ? 'https://admin.futurabroker.com/api' : 'http://localhost:3001/api'

const TOKEN_KEY = 'sigma_admin_token'

export type AppType = 'calux' | 'sigma' | 'aihakim'

export interface IntegrationEmail {
  id: number
  user_id: number
  email: string
  app?: AppType
  created_at: string
  user_name: string | null
  user_email: string | null
}

export interface IntegrationEmailsListData {
  items: IntegrationEmail[]
  total: number
  hasAppColumn: boolean
}

export interface AdminUser {
  id: number
  name: string
  email: string
  role: string
}

export interface LoginResult {
  token?: string
  user?: AdminUser
  requires2FA?: boolean
  requiresSetup2FA?: boolean
  challengeToken?: string
  message?: string
}

export class AdminUnauthorizedError extends Error {
  constructor(message = 'Sessão administrativa expirada') {
    super(message)
    this.name = 'AdminUnauthorizedError'
  }
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

async function request<T>(path: string, options: RequestInit = {}, withAuth = true): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (withAuth) {
    const token = getToken()
    if (!token) throw new AdminUnauthorizedError()
    headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(`${ADMIN_API_BASE}${path}`, { ...options, headers })

  let body: { success?: boolean; data?: T; error?: string; message?: string } | null = null
  try {
    body = await res.json()
  } catch {
    // resposta sem JSON (ex.: 502 de proxy) — cai no throw genérico abaixo
  }

  if (res.status === 401 && withAuth) {
    clearToken()
    throw new AdminUnauthorizedError(body?.error || 'Sessão administrativa expirada')
  }

  if (!res.ok || body?.success === false) {
    throw new Error(body?.error || `Falha na requisição (${res.status})`)
  }

  return body?.data as T
}

export const adminAccessService = {
  hasSession(): boolean {
    return !!getToken()
  },

  logout(): void {
    clearToken()
  },

  // Sem reCAPTCHA nesta página (decisão de 03/07/2026): o backend valida o
  // token apenas quando enviado; a proteção anti-brute-force é o throttle
  // por IP+email no admin-backend.
  async login(email: string, password: string): Promise<LoginResult> {
    const result = await request<LoginResult>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
      false
    )
    if (result?.token) setToken(result.token)
    return result
  },

  // O userId não trafega mais no corpo: o admin-backend só aceita o
  // challengeToken assinado emitido pelo /login (TTL 5min) — ver commit
  // "amarra 2FA ao login via challenge-token assinado".
  async verify2FA(challengeToken: string, code: string): Promise<LoginResult> {
    const result = await request<LoginResult>(
      '/auth/verify-2fa',
      { method: 'POST', body: JSON.stringify({ challengeToken, code }) },
      false
    )
    if (result?.token) setToken(result.token)
    return result
  },

  async listEmails(params: { page: number; limit: number; q?: string; app?: AppType }): Promise<IntegrationEmailsListData> {
    const qs = new URLSearchParams()
    qs.set('page', String(params.page))
    qs.set('limit', String(params.limit))
    if (params.q) qs.set('q', params.q)
    if (params.app) qs.set('app', params.app)
    return request(`/access-control/emails?${qs.toString()}`, { method: 'GET' })
  },

  async addEmail(data: { user_id: number; email: string; app: AppType }): Promise<IntegrationEmail> {
    return request('/access-control/emails', { method: 'POST', body: JSON.stringify(data) })
  },

  async removeEmail(id: number): Promise<void> {
    return request(`/access-control/emails/${id}`, { method: 'DELETE' })
  },
}
